import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  BarChart3,
  Target,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  Download,
  RefreshCw,
  TrendingUp,
  Plus,
  X,
  MessageSquare,
  UserPlus,
  Clock,
  Linkedin,
  Save,
  AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { OutreachService } from '../services/OutreachService';
import { LinkedInMetricsService } from '../services/LinkedInMetricsService';
import {
  OutreachContact,
  OutreachStatus,
  OutreachDashboardStats,
  LinkedInDailyMetrics,
  OUTREACH_STATUS_LABELS,
  OUTREACH_STATUS_COLORS,
  ALL_OUTREACH_STATUSES,
  PIPELINE_STATUSES,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  ICP_FIT_LABELS,
  ICP_FIT_COLORS,
  getContactDisplayName,
  ICPFit,
  Priority,
} from '../types/outreach';
import { toDate } from '../types/leads';
import { useAdminAuth } from '../contexts/AdminContext';

const TAB_ITEMS = [
  { label: 'Dashboard', icon: BarChart3 },
  { label: 'Pipeline', icon: Users },
  { label: 'LinkedIn Metrics', icon: Linkedin },
  { label: 'Follow-ups & Goals', icon: Target },
];

function formatDate(val: Date | { toDate: () => Date } | undefined): string {
  const d = toDate(val);
  if (d.getTime() === 0) return '-';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ============================================
// ADD CONTACT MODAL
// ============================================

const AddContactModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}> = ({ isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    linkedinUrl: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    birthday: '',
    estimatedBrokerCount: '',
    numberOfOffices: '',
    aggregator: '',
    icpFit: '' as ICPFit | '',
    priority: 'medium' as Priority,
    foundVia: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.company.trim()) {
      toast.error('First name and company are required');
      return;
    }
    setSaving(true);
    try {
      await OutreachService.createContact({
        ...form,
        icpFit: form.icpFit || undefined,
      });
      toast.success(`${form.firstName} ${form.lastName} added to pipeline`);
      setForm({
        firstName: '', lastName: '', company: '', role: '', linkedinUrl: '',
        email: '', phone: '', location: '', website: '', birthday: '',
        estimatedBrokerCount: '',
        numberOfOffices: '', aggregator: '', icpFit: '', priority: 'medium',
        foundVia: '', notes: '',
      });
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Error creating contact:', err);
      toast.error(err.message || 'Failed to create contact');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Add Contact</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">First Name *</label>
              <input
                type="text"
                value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Last Name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Company *</label>
              <input
                type="text"
                value={form.company}
                onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Role</label>
              <input
                type="text"
                value={form.role}
                onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                placeholder="e.g. Principal, MD, Operations Manager"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          {/* Contact details */}
          <div>
            <label className="text-sm text-slate-400 block mb-1">LinkedIn URL</label>
            <input
              type="text"
              value={form.linkedinUrl}
              onChange={e => setForm(f => ({ ...f, linkedinUrl: e.target.value }))}
              placeholder="https://www.linkedin.com/in/..."
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Location</label>
              <input
                type="text"
                value={form.location}
                onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="e.g. Gold Coast, QLD"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Website</label>
              <input
                type="text"
                value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder="https://www.example.com.au"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Birthday (year optional)</label>
              <input
                type="text"
                value={form.birthday}
                onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))}
                placeholder="e.g. 12 Mar or 12 Mar 1985"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          {/* Qualification */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Est. Broker Count</label>
              <input
                type="text"
                value={form.estimatedBrokerCount}
                onChange={e => setForm(f => ({ ...f, estimatedBrokerCount: e.target.value }))}
                placeholder="e.g. 5-10, 20+"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Offices</label>
              <input
                type="text"
                value={form.numberOfOffices}
                onChange={e => setForm(f => ({ ...f, numberOfOffices: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Aggregator</label>
              <input
                type="text"
                value={form.aggregator}
                onChange={e => setForm(f => ({ ...f, aggregator: e.target.value }))}
                placeholder="e.g. Connective, AFG, LMG"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">ICP Fit</label>
              <select
                value={form.icpFit}
                onChange={e => setForm(f => ({ ...f, icpFit: e.target.value as ICPFit | '' }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="">Select...</option>
                <option value="strong">Strong Fit</option>
                <option value="moderate">Moderate Fit</option>
                <option value="weak">Weak Fit</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value as Priority }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Found Via</label>
              <input
                type="text"
                value={form.foundVia}
                onChange={e => setForm(f => ({ ...f, foundVia: e.target.value }))}
                placeholder="e.g. linkedin_search, referral"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {saving ? 'Adding...' : 'Add Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================

const OutreachPage: React.FC = () => {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Pipeline state
  const [contacts, setContacts] = useState<OutreachContact[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OutreachStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<Priority | ''>('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Dashboard state
  const [stats, setStats] = useState<OutreachDashboardStats | null>(null);

  // Metrics state
  const [metricsHistory, setMetricsHistory] = useState<LinkedInDailyMetrics[]>([]);
  const [metricsForm, setMetricsForm] = useState<Partial<LinkedInDailyMetrics>>({});
  const [metricsDateKey, setMetricsDateKey] = useState(new Date().toISOString().split('T')[0]);
  const [savingMetrics, setSavingMetrics] = useState(false);

  // Follow-ups state
  const [followUps, setFollowUps] = useState<OutreachContact[]>([]);
  const [staleContacts, setStaleContacts] = useState<OutreachContact[]>([]);

  // Daily targets (stored in state, could be persisted to Firestore later)
  const [dailyTargets] = useState({
    connectionsSent: 5,
    dmsSent: 10,
    commentsLeft: 30,
    discoveryCallsWeek: 3,
  });

  const [loadedTabs, setLoadedTabs] = useState<Set<number>>(new Set());

  // ---- Data Loading ----

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashStats, allContacts] = await Promise.all([
        OutreachService.getDashboardStats(),
        OutreachService.getContacts(),
      ]);
      setStats(dashStats);
      setContacts(allContacts);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPipeline = useCallback(async () => {
    if (loadedTabs.has(1) && contacts.length > 0) return;
    try {
      setLoading(true);
      const data = await OutreachService.getContacts();
      setContacts(data);
      setLoadedTabs(prev => new Set(prev).add(1));
    } catch (err) {
      console.error('Error loading pipeline:', err);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [loadedTabs, contacts.length]);

  const loadMetrics = useCallback(async () => {
    if (loadedTabs.has(2)) return;
    try {
      setLoading(true);
      const [history, todayMetrics] = await Promise.all([
        LinkedInMetricsService.getRecentMetrics(30),
        LinkedInMetricsService.getMetrics(metricsDateKey),
      ]);
      setMetricsHistory(history);
      if (todayMetrics) {
        setMetricsForm({
          impressions: todayMetrics.impressions,
          reactions: todayMetrics.reactions,
          comments: todayMetrics.comments,
          reposts: todayMetrics.reposts,
          profileViews: todayMetrics.profileViews,
          searchAppearances: todayMetrics.searchAppearances,
          connectionsSent: todayMetrics.connectionsSent,
          connectionsAccepted: todayMetrics.connectionsAccepted,
          connectionsPending: todayMetrics.connectionsPending,
          dmsSent: todayMetrics.dmsSent,
          dmsReceived: todayMetrics.dmsReceived,
          commentsLeft: todayMetrics.commentsLeft,
          newContactsAdded: todayMetrics.newContactsAdded,
          discoveryCalls: todayMetrics.discoveryCalls,
        });
      }
      setLoadedTabs(prev => new Set(prev).add(2));
    } catch (err) {
      console.error('Error loading metrics:', err);
      toast.error('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, [loadedTabs, metricsDateKey]);

  const loadFollowUps = useCallback(async () => {
    if (loadedTabs.has(3)) return;
    try {
      setLoading(true);
      const [fups, stale] = await Promise.all([
        OutreachService.getFollowUpsDue(),
        OutreachService.getStaleContacts(),
      ]);
      setFollowUps(fups);
      setStaleContacts(stale);
      setLoadedTabs(prev => new Set(prev).add(3));
    } catch (err) {
      console.error('Error loading follow-ups:', err);
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  }, [loadedTabs]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === 1) loadPipeline();
    else if (activeTab === 2) loadMetrics();
    else if (activeTab === 3) loadFollowUps();
  }, [activeTab, loadPipeline, loadMetrics, loadFollowUps]);

  // ---- Handlers ----

  const handleRefresh = async () => {
    setLoadedTabs(new Set());
    if (activeTab === 0) await loadDashboard();
    else if (activeTab === 1) {
      const data = await OutreachService.getContacts();
      setContacts(data);
      setLoadedTabs(new Set([1]));
    } else if (activeTab === 2) {
      setLoadedTabs(new Set());
      await loadMetrics();
    } else if (activeTab === 3) {
      setLoadedTabs(new Set());
      await loadFollowUps();
    }
    toast.success('Data refreshed');
  };

  const handleExportCSV = async () => {
    try {
      const csv = await OutreachService.exportToCSV();
      if (!csv) {
        toast.error('No data to export');
        return;
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `outreach-contacts-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      console.error('Error exporting CSV:', err);
      toast.error('Failed to export CSV');
    }
  };

  const handleSaveMetrics = async () => {
    setSavingMetrics(true);
    try {
      await LinkedInMetricsService.saveMetrics(metricsDateKey, metricsForm);
      const history = await LinkedInMetricsService.getRecentMetrics(30);
      setMetricsHistory(history);
      toast.success(`Metrics saved for ${metricsDateKey}`);
    } catch (err) {
      console.error('Error saving metrics:', err);
      toast.error('Failed to save metrics');
    } finally {
      setSavingMetrics(false);
    }
  };

  const handleMetricsDateChange = async (dateKey: string) => {
    setMetricsDateKey(dateKey);
    const existing = await LinkedInMetricsService.getMetrics(dateKey);
    if (existing) {
      setMetricsForm({
        impressions: existing.impressions,
        reactions: existing.reactions,
        comments: existing.comments,
        reposts: existing.reposts,
        profileViews: existing.profileViews,
        searchAppearances: existing.searchAppearances,
        connectionsSent: existing.connectionsSent,
        connectionsAccepted: existing.connectionsAccepted,
        connectionsPending: existing.connectionsPending,
        dmsSent: existing.dmsSent,
        dmsReceived: existing.dmsReceived,
        commentsLeft: existing.commentsLeft,
        newContactsAdded: existing.newContactsAdded,
        discoveryCalls: existing.discoveryCalls,
      });
    } else {
      setMetricsForm({});
    }
  };

  // ---- Filtering ----

  const filterContacts = (list: OutreachContact[]): OutreachContact[] => {
    let filtered = list;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c => {
        const name = getContactDisplayName(c).toLowerCase();
        const company = (c.company || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const url = (c.linkedinUrl || '').toLowerCase();
        return name.includes(q) || company.includes(q) || email.includes(q) || url.includes(q);
      });
    }
    if (statusFilter) {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    if (priorityFilter) {
      filtered = filtered.filter(c => c.priority === priorityFilter);
    }
    return filtered;
  };

  // ---- Render Helpers ----

  const renderStatusBadge = (status: OutreachStatus) => {
    const color = OUTREACH_STATUS_COLORS[status] || '#6b7280';
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {OUTREACH_STATUS_LABELS[status] || status}
      </span>
    );
  };

  const renderPriorityBadge = (priority?: Priority) => {
    if (!priority) return null;
    const color = PRIORITY_COLORS[priority];
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {PRIORITY_LABELS[priority]}
      </span>
    );
  };

  const renderICPBadge = (fit?: ICPFit) => {
    if (!fit) return <span className="text-slate-500">-</span>;
    const color = ICP_FIT_COLORS[fit];
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {ICP_FIT_LABELS[fit]}
      </span>
    );
  };

  const renderLoadingSpinner = () => (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500 mx-auto"></div>
        <p className="mt-4 text-slate-400">Loading...</p>
      </div>
    </div>
  );

  // ---- Progress Bar Helper ----
  const renderProgressBar = (current: number, target: number, label: string, color: string) => {
    const pct = Math.min(100, Math.round((current / target) * 100));
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">{label}</span>
          <span className="text-sm font-semibold text-white">{current} / {target}</span>
        </div>
        <div className="w-full bg-slate-700 rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full transition-all"
            style={{ width: `${pct}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  // ============================================
  // TAB 0: DASHBOARD
  // ============================================

  const renderDashboard = () => {
    if (!stats) return renderLoadingSpinner();

    // Funnel chart data
    const funnelData = PIPELINE_STATUSES.map(s => ({
      name: OUTREACH_STATUS_LABELS[s],
      count: stats.byStatus[s] || 0,
    }));

    // Today's metrics for progress bars
    const todayMetrics = metricsHistory.find(m => m.date === new Date().toISOString().split('T')[0]);

    return (
      <div className="space-y-6">
        {/* Daily Targets Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {renderProgressBar(
            todayMetrics?.connectionsSent || 0,
            dailyTargets.connectionsSent,
            'Connections Sent Today',
            '#3b82f6'
          )}
          {renderProgressBar(
            todayMetrics?.dmsSent || 0,
            dailyTargets.dmsSent,
            'DMs Sent Today',
            '#8b5cf6'
          )}
          {renderProgressBar(
            todayMetrics?.commentsLeft || 0,
            dailyTargets.commentsLeft,
            'Comments Left Today ($1.80)',
            '#06b6d4'
          )}
          {renderProgressBar(
            todayMetrics?.discoveryCalls || 0,
            dailyTargets.discoveryCallsWeek,
            'Discovery Calls This Week',
            '#22c55e'
          )}
        </div>

        {/* KPI Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-sky-500 to-blue-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Total Pipeline</p>
                <Users className="w-5 h-5 text-sky-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.totalContacts}</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Response Rate</p>
                <MessageSquare className="w-5 h-5 text-amber-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.responseRate}%</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">This Week</p>
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.contactsThisWeek}</p>
            </div>
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Conversion Rate</p>
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-3xl font-bold text-white mt-2">{stats.conversionRate}%</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Funnel */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Pipeline Funnel</h3>
            {funnelData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnelData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 11 }} width={120} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#f8fafc',
                    }}
                  />
                  <Bar dataKey="count" fill="#0077b5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No pipeline data yet</p>
            )}
          </div>

          {/* LinkedIn Metrics Trend */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">LinkedIn Metrics (30 days)</h3>
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    tickFormatter={v => v.substring(5)}
                  />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #475569',
                      borderRadius: '8px',
                      color: '#f8fafc',
                    }}
                  />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
                  <Line type="monotone" dataKey="impressions" stroke="#0077b5" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="profileViews" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No metrics data yet. Enter daily metrics in the LinkedIn Metrics tab.</p>
            )}
          </div>
        </div>

        {/* Follow-ups Due Today */}
        {stats.followUpsDueToday.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold text-white">Follow-ups Due Today</h3>
              <span className="ml-auto text-sm text-amber-400 font-medium">{stats.followUpsDueToday.length}</span>
            </div>
            <div className="divide-y divide-slate-700/50">
              {stats.followUpsDueToday.map(c => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/outreach/${c.id}`)}
                  className="px-6 py-3 flex items-center justify-between hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{getContactDisplayName(c)}</span>
                    <span className="text-slate-400 ml-2">{c.company}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {renderPriorityBadge(c.priority)}
                    {renderStatusBadge(c.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // TAB 1: PIPELINE
  // ============================================

  const renderPipeline = () => {
    const filtered = filterContacts(contacts);

    return (
      <div className="space-y-4">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search name, company, LinkedIn URL..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as OutreachStatus | '')}
                className="pl-10 pr-8 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white appearance-none focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500"
              >
                <option value="">All Statuses</option>
                {ALL_OUTREACH_STATUSES.map(s => (
                  <option key={s} value={s}>{OUTREACH_STATUS_LABELS[s]}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                value={priorityFilter}
                onChange={e => setPriorityFilter(e.target.value as Priority | '')}
                className="px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white appearance-none focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500"
              >
                <option value="">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Contact
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Company</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Role</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Priority</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">ICP</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Last Contact</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Follow-up</th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">Acts</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filtered.map(contact => (
                  <tr
                    key={contact.id}
                    onClick={() => navigate(`/outreach/${contact.id}`)}
                    className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-white font-medium">
                      {getContactDisplayName(contact)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-300">{contact.company}</td>
                    <td className="px-6 py-4 text-sm text-slate-300">{contact.role || '-'}</td>
                    <td className="px-6 py-4 text-sm">{renderStatusBadge(contact.status)}</td>
                    <td className="px-6 py-4 text-sm">{renderPriorityBadge(contact.priority)}</td>
                    <td className="px-6 py-4 text-sm">{renderICPBadge(contact.icpFit)}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">{formatDate(contact.lastContactedAt)}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {contact.nextFollowUpDate ? (
                        <span className={
                          toDate(contact.nextFollowUpDate) <= new Date() ? 'text-amber-400 font-medium' : ''
                        }>
                          {formatDate(contact.nextFollowUpDate)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-center text-slate-400">{contact.activityCount || 0}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-400">
                      {searchQuery || statusFilter || priorityFilter ? 'No contacts match your filters' : 'No outreach contacts yet. Click "Add Contact" to start.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // TAB 2: LINKEDIN METRICS
  // ============================================

  const renderMetrics = () => {
    const aggregated = LinkedInMetricsService.aggregateMetrics(metricsHistory);

    return (
      <div className="space-y-6">
        {/* Daily Entry Form */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Daily Metrics Entry</h3>
            <input
              type="date"
              value={metricsDateKey}
              onChange={e => handleMetricsDateChange(e.target.value)}
              className="bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {([
              { key: 'impressions', label: 'Impressions' },
              { key: 'reactions', label: 'Reactions' },
              { key: 'comments', label: 'Comments' },
              { key: 'reposts', label: 'Reposts' },
              { key: 'profileViews', label: 'Profile Views' },
              { key: 'searchAppearances', label: 'Search Appearances' },
              { key: 'connectionsSent', label: 'Connections Sent' },
              { key: 'connectionsAccepted', label: 'Connections Accepted' },
              { key: 'connectionsPending', label: 'Connections Pending' },
              { key: 'dmsSent', label: 'DMs Sent' },
              { key: 'dmsReceived', label: 'DMs Received' },
              { key: 'commentsLeft', label: 'Comments Left' },
              { key: 'newContactsAdded', label: 'New Contacts' },
              { key: 'discoveryCalls', label: 'Discovery Calls' },
            ] as { key: keyof LinkedInDailyMetrics; label: string }[]).map(field => (
              <div key={field.key}>
                <label className="text-xs text-slate-400 block mb-1">{field.label}</label>
                <input
                  type="number"
                  min="0"
                  value={(metricsForm as any)[field.key] ?? ''}
                  onChange={e => setMetricsForm(f => ({
                    ...f,
                    [field.key]: e.target.value === '' ? undefined : parseInt(e.target.value),
                  }))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            ))}
          </div>

          <button
            onClick={handleSaveMetrics}
            disabled={savingMetrics}
            className="mt-4 flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {savingMetrics ? 'Saving...' : 'Save Metrics'}
          </button>
        </div>

        {/* Aggregated Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[
            { label: 'Total Impressions', value: aggregated.totalImpressions.toLocaleString(), color: 'sky' },
            { label: 'Total Reactions', value: aggregated.totalReactions.toLocaleString(), color: 'amber' },
            { label: 'Avg Profile Views/Day', value: aggregated.avgProfileViews.toString(), color: 'green' },
            { label: 'Connection Accept Rate', value: `${aggregated.connectionAcceptanceRate}%`, color: 'purple' },
            { label: 'Total DMs Sent', value: aggregated.totalDmsSent.toLocaleString(), color: 'blue' },
          ].map(stat => (
            <div key={stat.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">{stat.label}</p>
              <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Trend Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Impressions & Profile Views</h3>
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => v.substring(5)} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }} />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
                  <Line type="monotone" dataKey="impressions" stroke="#0077b5" strokeWidth={2} dot={false} name="Impressions" />
                  <Line type="monotone" dataKey="profileViews" stroke="#f59e0b" strokeWidth={2} dot={false} name="Profile Views" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No data yet</p>
            )}
          </div>

          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Connection & Outreach Activity</h3>
            {metricsHistory.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={v => v.substring(5)} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#f8fafc' }} />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 12 }} />
                  <Bar dataKey="connectionsSent" fill="#3b82f6" name="Connections Sent" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="dmsSent" fill="#8b5cf6" name="DMs Sent" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="commentsLeft" fill="#06b6d4" name="Comments" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">No data yet</p>
            )}
          </div>
        </div>

        {/* Metrics Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">Daily Log (Last 30 Days)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Impressions</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Reactions</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Comments</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Profile Views</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Conn Sent</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Conn Accepted</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">DMs Sent</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase">Comments Left</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {[...metricsHistory].reverse().map(m => (
                  <tr key={m.date} className="hover:bg-slate-700/50">
                    <td className="px-4 py-3 text-sm text-white">{m.date}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.impressions ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.reactions ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.comments ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.profileViews ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.connectionsSent ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.connectionsAccepted ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.dmsSent ?? '-'}</td>
                    <td className="px-4 py-3 text-sm text-right text-slate-300">{m.commentsLeft ?? '-'}</td>
                  </tr>
                ))}
                {metricsHistory.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-400">
                      No metrics recorded yet. Use the form above to enter daily data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // TAB 3: FOLLOW-UPS & GOALS
  // ============================================

  const renderFollowUps = () => {
    const deferredContacts = contacts.filter(c => c.status === 'deferred');

    return (
      <div className="space-y-6">
        {/* Weekly Goals */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-sky-400" />
            Daily Targets
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <p className="text-xs text-slate-400 mb-1">Connections / Day</p>
              <p className="text-2xl font-bold text-white">{dailyTargets.connectionsSent}</p>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <p className="text-xs text-slate-400 mb-1">DMs / Day</p>
              <p className="text-2xl font-bold text-white">{dailyTargets.dmsSent}</p>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <p className="text-xs text-slate-400 mb-1">Comments / Day ($1.80)</p>
              <p className="text-2xl font-bold text-white">{dailyTargets.commentsLeft}</p>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
              <p className="text-xs text-slate-400 mb-1">Discovery Calls / Week</p>
              <p className="text-2xl font-bold text-white">{dailyTargets.discoveryCallsWeek}</p>
            </div>
          </div>
        </div>

        {/* Follow-ups Due */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-semibold text-white">Follow-ups Due</h3>
            <span className="ml-auto text-sm text-amber-400 font-medium">{followUps.length}</span>
          </div>
          {followUps.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {followUps.map(c => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/outreach/${c.id}`)}
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{getContactDisplayName(c)}</span>
                    <span className="text-slate-400 ml-2 text-sm">{c.company}</span>
                    {c.role && <span className="text-slate-500 ml-2 text-sm">({c.role})</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">Due: {formatDate(c.nextFollowUpDate)}</span>
                    {renderPriorityBadge(c.priority)}
                    {renderStatusBadge(c.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-slate-400">No follow-ups due</div>
          )}
        </div>

        {/* Stale Contacts */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <h3 className="text-lg font-semibold text-white">Stale Contacts (7+ days, no follow-up set)</h3>
            <span className="ml-auto text-sm text-red-400 font-medium">{staleContacts.length}</span>
          </div>
          {staleContacts.length > 0 ? (
            <div className="divide-y divide-slate-700/50">
              {staleContacts.map(c => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/outreach/${c.id}`)}
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{getContactDisplayName(c)}</span>
                    <span className="text-slate-400 ml-2 text-sm">{c.company}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">Last: {formatDate(c.lastContactedAt)}</span>
                    {renderStatusBadge(c.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-slate-400">No stale contacts</div>
          )}
        </div>

        {/* Deferred Contacts */}
        {deferredContacts.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-white">Deferred Contacts</h3>
              <span className="ml-auto text-sm text-slate-400 font-medium">{deferredContacts.length}</span>
            </div>
            <div className="divide-y divide-slate-700/50">
              {deferredContacts.map(c => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/outreach/${c.id}`)}
                  className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <div>
                    <span className="text-white font-medium">{getContactDisplayName(c)}</span>
                    <span className="text-slate-400 ml-2 text-sm">{c.company}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">Added: {formatDate(c.createdAt)}</span>
                    {renderPriorityBadge(c.priority)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-blue-600 to-indigo-700 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Linkedin className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">LinkedIn Outreach</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Outbound pipeline, activity tracking, and LinkedIn analytics
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              <span className="text-sm">Pipeline management</span>
            </div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="text-sm">LinkedIn metrics</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span className="text-sm">Daily goals</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation + Refresh */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
          {TAB_ITEMS.map((tab, index) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.label}
                onClick={() => {
                  setActiveTab(index);
                  setSearchQuery('');
                  setStatusFilter('');
                  setPriorityFilter('');
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === index
                    ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tab Content */}
      {loading && activeTab === 0 && !stats && renderLoadingSpinner()}
      {loading && activeTab === 1 && !loadedTabs.has(1) && renderLoadingSpinner()}
      {loading && activeTab === 2 && !loadedTabs.has(2) && renderLoadingSpinner()}
      {loading && activeTab === 3 && !loadedTabs.has(3) && renderLoadingSpinner()}

      {activeTab === 0 && stats && renderDashboard()}
      {activeTab === 1 && renderPipeline()}
      {activeTab === 2 && renderMetrics()}
      {activeTab === 3 && renderFollowUps()}

      {/* Add Contact Modal */}
      <AddContactModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={async () => {
          const data = await OutreachService.getContacts();
          setContacts(data);
          if (stats) {
            const newStats = await OutreachService.getDashboardStats();
            setStats(newStats);
          }
        }}
      />
    </div>
  );
};

export default OutreachPage;
